import {
  Dispatch,
  SetStateAction,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { Platform, Pressable as PressableRN } from 'react-native'

import { publishEvent } from '@zet/analytics'
import {
  BTN,
  BottomSheet,
  Button,
  H6,
  Icon,
  SH3,
  Stack,
  XStack,
  colors,
} from '@zet/design-system'
import { Checkbox } from '@zet/design-system/checkbox'
import Pressable from '@zet/design-system/pressable'
import { i18n } from '@zet/localization'
import useGetAgentDetailsAndTheme from '@zet/zet-pro-quick-journey/hooks/useGetAgentDetailsAndTheme'
import { ScrollView } from 'react-native-gesture-handler'
import { useShallow } from 'zustand/react/shallow'

import { useFontFlowStore } from '../../../store/fontFlowStore'
import useFilterStore from '../../hooks/useFilterStore'
import RadioButton from '../radioButton'

export type SubFilterState = {
  [key: string]: string[] | string | undefined
}

export type SubFilterValuesState = {
  [key: string]: string[] | string | undefined
}

export type SubFiltersBSRef = {
  resetLocalState: () => void
  updateLcalState: (args: {
    filterState: string
    subFIlterState: SubFilterState
    shouldTriggerSubmit: boolean
  }) => void
  show: () => void
  hide: () => void
}

export type SubFilterData = {
  key: string
  type: 'SUB_FILTER' | 'DROPDOWN' | 'QUICK_FILTER'
  displayText: string
  values?: {
    key: string
    displayText: string
    isMultiSelect: boolean
    values?: {
      displayText: string
      key: string
    }[]
  }[]
}

type SubFiltersBottomSheetProps = {
  initialState?: SubFilterState
  data?: SubFilterData
  // isVisible: boolean
  onDismiss?: () => void
  onSubmit?: (selectedFilters: SubFilterState) => void
  onChange?: (selectedFilters: SubFilterState) => void
  onClear?: () => void
  categoryId?: string | number
  customerId?: string | number
}

const isWeb = Platform.OS === 'web'

const generateSnapPoints = (
  _minHeight: string | number,
  _maxHeight: string | number
) => {
  const maxHeight = Number(_maxHeight)

  const firstPoint = Math.min(Math.max(400, Number(maxHeight) * 0.7), maxHeight)
  const secondPoint = Math.max(
    firstPoint,
    Math.min(Number(maxHeight) * 0.9, maxHeight)
  )
  return [Math.floor(firstPoint), Math.floor(secondPoint)]
}

const Header = ({
  heading,
  onDismiss,
}: {
  heading?: string
  onDismiss?: () => void
}) => {
  if (!heading) {
    return <></>
  }
  return (
    <XStack
      key={heading}
      className={`items-center justify-between ${
        isWeb ? '' : 'border-grays-40 border-b px-4 py-2'
      }`}
    >
      <XStack>
        <Icon type="Preferences" className="mr-4/2" />
        <H6>{heading}</H6>
      </XStack>
      <PressableRN onPress={onDismiss}>
        <Icon type="Close" />
      </PressableRN>
    </XStack>
  )
}

type BodyProps = {
  categoryId?: string | number
  customerId?: string | number
  filters: SubFilterData['values']
  selectedFilter: string | undefined
  setSelectedFilter: Dispatch<SetStateAction<string | undefined>>
  selectedSubFilters: SubFilterState
  setSelectedSubFilters: Dispatch<SetStateAction<SubFilterState>>
  setSelectedFilterValues: Dispatch<SetStateAction<SubFilterState>>
}

const Body = ({
  filters,
  selectedFilter,
  setSelectedFilter,
  selectedSubFilters,
  setSelectedSubFilters,
  setSelectedFilterValues,
  categoryId,
  customerId,
}: BodyProps) => {
  const { isBMSFlow, isJfsFlow } = useGetAgentDetailsAndTheme()
  const currentlyVisibleSubFilters = useMemo(
    () => filters?.find((subFilter) => subFilter?.key === selectedFilter),
    [filters, selectedFilter]
  )

  const onChangeHandler = ({
    isSelected,
    isMultiSelect,
    currentlyVisibleFilterKey,
    subFilterKey,
    subFilterValue,
  }: {
    isSelected: boolean
    isMultiSelect: boolean
    currentlyVisibleFilterKey: string
    subFilterKey: string
    subFilterValue: string
  }) => {
    setSelectedSubFilters((prevSelectedSubFilters) => {
      let updatedSubFilterValues

      const currentlySelectedFilterValues =
        prevSelectedSubFilters?.[currentlyVisibleFilterKey] ||
        (isMultiSelect ? [] : '') // Added the deafult values

      if (isSelected) {
        // Remove the selected sub-filter(s) based on isMultiSelect flag
        updatedSubFilterValues =
          isMultiSelect && Array.isArray(currentlySelectedFilterValues)
            ? currentlySelectedFilterValues?.filter(
                (_subFilterKey) => _subFilterKey !== subFilterKey
              )
            : undefined // Set to undefined if it's not multi-select
      } else {
        // Add the selected sub-filter(s) based on isMultiSelect flag
        updatedSubFilterValues = isMultiSelect
          ? [...currentlySelectedFilterValues, subFilterKey]
          : subFilterKey
      }

      return {
        ...prevSelectedSubFilters,
        [currentlyVisibleFilterKey]: updatedSubFilterValues,
      }
    })
    if (isJfsFlow) {
      setSelectedFilterValues((prevSelectedSubFilters) => {
        let updatedSubFilterValues

        const currentlySelectedFilterValues =
          prevSelectedSubFilters?.[currentlyVisibleFilterKey] ||
          (isMultiSelect ? [] : '')

        if (isSelected) {
          updatedSubFilterValues =
            isMultiSelect && Array.isArray(currentlySelectedFilterValues)
              ? currentlySelectedFilterValues?.filter(
                  (_subFilterKey) => _subFilterKey !== subFilterValue
                )
              : undefined
        } else {
          updatedSubFilterValues = isMultiSelect
            ? [...currentlySelectedFilterValues, subFilterValue]
            : subFilterValue
        }

        return {
          ...prevSelectedSubFilters,
          [currentlyVisibleFilterKey]: updatedSubFilterValues,
        }
      })
    }
  }

  return (
    <Stack className="px-4">
      <XStack>
        <Stack className="border-grays-40 w-1/3 space-y-2 border-r py-2">
          {filters?.map((filter) => {
            return (
              <H6
                key={filter?.displayText}
                onPress={() => {
                  setSelectedFilter(filter?.key)
                  if (filter?.key)
                    publishEvent('click_action', {
                      item_name: `filter_option_new_${
                        Platform.OS === 'web'
                          ? 'recommendation_web'
                          : customerId
                          ? 'recommendation_app'
                          : 'brand_list'
                      }`,
                      product: categoryId,
                      option: filter?.key,
                    })
                }}
                className={` ${
                  isJfsFlow ? 'border-warning-100' : 'border-primary-100'
                }  py-3 ${
                  selectedFilter === filter?.key
                    ? `${
                        isJfsFlow ? 'text-warning-100' : 'text-primary-100'
                      }  border-r-4`
                    : 'text-grays-80'
                }`}
              >
                {filter?.displayText}
              </H6>
            )
          })}
        </Stack>

        <ScrollView
          key={selectedFilter}
          className="max-h-8 w-2/3 py-2 pl-4"
          showsVerticalScrollIndicator={false}
          style={{
            maxHeight: 500,
          }}
          contentContainerStyle={{
            paddingBottom: 20,
            rowGap: 8,
          }}
        >
          {currentlyVisibleSubFilters?.values?.map((subFilter) => {
            const currentlyVisibleFilterKey = currentlyVisibleSubFilters?.key

            const subFilterKey = subFilter?.key

            const isMultiSelect = !!currentlyVisibleSubFilters?.isMultiSelect

            const isSelected =
              !!selectedSubFilters[currentlyVisibleFilterKey]?.includes(
                subFilterKey
              )

            return (
              <Pressable
                key={subFilter?.key}
                className={`${
                  isBMSFlow || isJfsFlow ? 'bg-grays-20' : 'bg-primary-10'
                } flex-row items-center  rounded border  p-3 ${
                  isSelected && !isJfsFlow
                    ? 'border-primary-50'
                    : 'border-transparent'
                }`}
                onPress={() => {
                  onChangeHandler({
                    isSelected,
                    currentlyVisibleFilterKey,
                    subFilterKey: subFilter?.key,
                    subFilterValue: subFilter?.displayText,
                    isMultiSelect,
                  })

                  !isSelected &&
                    publishEvent('click_action', {
                      item_name: `filter_selection_new_${
                        Platform.OS === 'web'
                          ? 'recommendation_web'
                          : customerId
                          ? 'recommendation_app'
                          : 'brand_list'
                      }`,
                      product: categoryId,
                      option: currentlyVisibleSubFilters?.key,
                      selected_option: subFilter?.key,
                    })
                }}
              >
                {isMultiSelect ? (
                  <Checkbox
                    accesibilityLabel=""
                    checked={isSelected}
                    color={
                      isSelected
                        ? isJfsFlow
                          ? colors.tertiary[100]
                          : colors.primary[100]
                        : colors.grays[70]
                    }
                    onChange={() =>
                      onChangeHandler({
                        isSelected,
                        currentlyVisibleFilterKey,
                        subFilterKey: subFilter?.key,
                        isMultiSelect,
                        subFilterValue: subFilter?.displayText,
                      })
                    }
                  />
                ) : (
                  <RadioButton isChecked={isSelected} size={24} />
                )}

                <SH3
                  className={`ml-2 mr-3 ${
                    isSelected
                      ? isJfsFlow
                        ? 'text-tertiary-100'
                        : 'text-primary-100'
                      : 'text-grays-80'
                  }`}
                >
                  {subFilter?.displayText}
                </SH3>
              </Pressable>
            )
          })}
        </ScrollView>
      </XStack>
    </Stack>
  )
}

type FooterProps = {
  customerId: string | number | undefined
  onSubmit?: (selectedFilters: SubFilterState) => void
  onClear?: () => void
  selectedSubFilters: SubFilterState
}

const Footer = ({
  customerId,
  onClear,
  onSubmit,
  selectedSubFilters,
}: FooterProps) => {
  const { isJfsFlow } = useFontFlowStore()

  return (
    <XStack
      className={`justify-between ${
        isWeb ? '' : 'border-grays-30 border-t-2 p-2 '
      }`}
    >
      <Pressable
        className={`${
          isJfsFlow ? 'bg-primary-100' : 'bg-grays-20'
        } mr-1 flex-1 items-center ${
          isJfsFlow ? 'rounded-3xl' : 'rounded'
        } px-4 py-2`}
        onPress={() => {
          publishEvent('click_action', {
            item_name: `clear_filter_${
              isWeb
                ? 'recommendation_web'
                : customerId
                ? 'recommendation_app'
                : 'brand_list'
            }`,
          })
          onClear?.()
        }}
      >
        <BTN className="text-black">
          {i18n.t('brandDetails.filters.bottomSheet.clearFilter')}
        </BTN>
      </Pressable>
      <Button
        className={`ml-1 flex-1 ${isJfsFlow ? 'rounded-3xl' : 'rounded'}`}
        onPress={() => {
          publishEvent('click_action', {
            item_name: `apply_filter_${
              isWeb
                ? 'recommendation_web'
                : customerId
                ? 'recommendation_app'
                : 'brand_list'
            }`,
          })
          onSubmit?.(selectedSubFilters)
        }}
      >
        <BTN className={`${isJfsFlow ? 'text-tertiary-100' : 'text-white'}`}>
          {i18n.t('brandDetails.filters.bottomSheet.applyFilter')}
        </BTN>
      </Button>
    </XStack>
  )
}

const SubFiltersBottomSheet = (
  {
    data,
    onDismiss,
    onSubmit,
    onChange,
    onClear,
    categoryId,
    customerId,
  }: SubFiltersBottomSheetProps,
  ref: SubFiltersBSRef
) => {
  const [isVisible, setIsVisible] = useState(false)
  const [selectedFilter, setSelectedFilter] = useState<string | undefined>()

  const {
    selectedSubFilters,
    setSelectedSubFilters,
    setSelectedFilterValues,
    resetSubFilterToPrevState,
  } = useFilterStore(
    useShallow((state) => ({
      selectedSubFilters: state?.localSubFilters,
      setSelectedSubFilters: state?.setLocalSubFilter,
      resetSubFilterToPrevState: state?.resetSubFilterToPrevState,
      setSelectedFilterValues: state?.setSelectedFilterValues,
    }))
  )

  const _onDismiss = useCallback(() => {
    setIsVisible(false)
    onDismiss?.()
    resetSubFilterToPrevState?.()
  }, [onDismiss, resetSubFilterToPrevState])

  useEffect(() => {
    // sets the first filter to show
    if (data?.values?.[0]?.key) {
      setSelectedFilter(data?.values?.[0]?.key)
    }
  }, [data?.values])

  useEffect(() => {
    if (Object.keys(selectedSubFilters)?.length > 0) {
      onChange?.(selectedSubFilters)
    }
  }, [onChange, selectedSubFilters])

  useImperativeHandle(
    ref,
    () => {
      return {
        resetLocalState() {
          const firstFilterKey = data?.values?.[0]?.key
          setSelectedFilter(firstFilterKey)
          setSelectedSubFilters({})
        },
        updateLcalState({
          filterState,
          subFIlterState,
          shouldTriggerSubmit,
        }: Parameters<SubFiltersBSRef['updateLcalState']>[number]) {
          setSelectedFilter(filterState)
          setSelectedSubFilters(subFIlterState)
          shouldTriggerSubmit && onSubmit?.(subFIlterState)
        },
        show: () => setIsVisible(true),
        hide: () => setIsVisible(false),
      }
    },
    [data?.values, onSubmit]
  )

  if (Platform.OS === 'web') {
    return (
      <BottomSheet
        visible={isVisible}
        onDismiss={_onDismiss}
        header={<Header heading={data?.displayText} onDismiss={_onDismiss} />}
        footer={
          <Footer
            customerId={customerId}
            selectedSubFilters={selectedSubFilters}
            onClear={onClear}
            onSubmit={onSubmit}
          />
        }
        snapPoints={generateSnapPoints}
        sheetName={'filter_new_recommendation_web'}
        eventPayload={{ product: categoryId }}
      >
        <Body
          key={data?.key}
          filters={data?.values}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          selectedSubFilters={selectedSubFilters}
          setSelectedSubFilters={setSelectedSubFilters}
          setSelectedFilterValues={setSelectedFilterValues}
        />
      </BottomSheet>
    )
  }

  return (
    <BottomSheet
      visible={isVisible}
      onDismiss={_onDismiss}
      sheetName={`filter_new_${
        customerId ? 'recommendation_app' : 'brand_list'
      }`}
      eventPayload={{ product: categoryId }}
    >
      <Stack>
        <Header heading={data?.displayText} onDismiss={_onDismiss} />
        <Body
          categoryId={categoryId}
          customerId={customerId}
          filters={data?.values}
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          selectedSubFilters={selectedSubFilters}
          setSelectedSubFilters={setSelectedSubFilters}
        />
        <Footer
          customerId={customerId}
          selectedSubFilters={selectedSubFilters}
          onClear={onClear}
          onSubmit={onSubmit}
        />
      </Stack>
    </BottomSheet>
  )
}

export default forwardRef(SubFiltersBottomSheet)
